import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Receipt, User, FolderOpen } from 'lucide-react';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { getCustomerDisplayName } from '@/utils/customerDisplayHelpers';

interface InvoiceDetailsCardProps {
  invoice: any;
  linkedProject?: any;
}

const InvoiceDetailsCard: React.FC<InvoiceDetailsCardProps> = ({ invoice, linkedProject }) => {
  const navigate = useNavigate();

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'draft':
        return <Badge variant="outline">Draft</Badge>;
      case 'sent':
        return <Badge variant="secondary">Sent</Badge>;
      case 'paid':
        return <Badge variant="default" className="bg-success text-success-foreground">Paid</Badge>;
      case 'overdue':
        return <Badge variant="destructive">Overdue</Badge>;
      case 'cancelled':
        return <Badge variant="destructive">Cancelled</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const handleCustomerClick = () => {
    if (invoice?.customer_id) {
      navigate(`/customers?customer=${invoice.customer_id}`);
    }
  };

  const handleEstimateClick = () => {
    if (invoice?.estimates?.id) {
      navigate(`/estimates/${invoice.estimates.id}`);
    }
  };

  const handleProjectClick = () => {
    if (linkedProject) {
      navigate(`/projects/${linkedProject.id}`);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Receipt className="h-5 w-5" />
          Invoice Details
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <h2 className="text-2xl font-bold">{invoice.title}</h2>
          <div className="flex items-center gap-2 mt-1">
            {getStatusBadge(invoice.status)}
          </div>
        </div>
        <div>
          <h3 className="text-sm font-medium text-muted-foreground">Invoice Number</h3>
          <p className="font-mono text-lg font-semibold">{invoice.invoice_number || 'INV-PENDING'}</p>
        </div>

        {/* Related Information Section */}
        <div className="space-y-3">
          <h3 className="text-sm font-medium text-muted-foreground">Related Information</h3>
          
          {/* Customer Relationship */}
          {invoice.customers && (
            <div className="p-3 bg-primary/5 rounded-lg border border-primary/20">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <User className="h-4 w-4 text-primary flex-shrink-0" />
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{getCustomerDisplayName(invoice.customers)}</div>
                    {invoice.customers.emails && invoice.customers.emails.length > 0 && (
                      <div className="text-xs text-muted-foreground truncate">{invoice.customers.emails[0]}</div>
                    )}
                  </div>
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={handleCustomerClick}
                  className="w-full sm:w-auto"
                >
                  View Customer
                </Button>
              </div>
            </div>
          )}

          {/* Associated Estimate */}
          {invoice.estimates && (
            <div className="p-3 bg-success/5 rounded-lg border border-success/20">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <Receipt className="h-4 w-4 text-success flex-shrink-0" />
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{invoice.estimates.estimate_number}</div>
                    <div className="text-xs text-muted-foreground truncate">{invoice.estimates.title}</div>
                  </div>
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={handleEstimateClick}
                  className="w-full sm:w-auto"
                >
                  View Estimate
                </Button>
              </div>
            </div>
          )}

          {/* Linked Project */}
          {linkedProject && (
            <div className="p-3 bg-accent/50 rounded-lg border border-accent">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <FolderOpen className="h-4 w-4 text-accent-foreground flex-shrink-0" />
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{linkedProject.name}</div>
                    {linkedProject.status && (
                      <div className="text-xs text-muted-foreground">Status: {linkedProject.status}</div>
                    )}
                  </div>
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={handleProjectClick}
                  className="w-full sm:w-auto"
                >
                  View Project
                </Button>
              </div>
            </div>
          )}
        </div>

        {invoice.invoice_type && invoice.invoice_type !== 'full' && (
          <div>
            <h3 className="text-sm font-medium text-muted-foreground">Invoice Type</h3>
            <Badge variant="outline">
              {invoice.invoice_type === 'percentage' && invoice.percentage_of_estimate
                ? `${invoice.percentage_of_estimate}% of Estimate`
                : invoice.invoice_type.replace('_', ' ').toUpperCase()
              }
            </Badge>
          </div>
        )}
        {invoice.description && (
          <div>
            <h3 className="text-sm font-medium text-muted-foreground">Description</h3>
            <p>{invoice.description}</p>
          </div>
        )}
        <div>
          <h3 className="text-sm font-medium text-muted-foreground">Created</h3>
          <p><p>{format(new Date(invoice.created_at), 'dd MMMM yyyy')}</p></p>
        </div>
        {invoice.due_date && (
          <div>
            <h3 className="text-sm font-medium text-muted-foreground">Due Date</h3>
            <p className={new Date() > new Date(invoice.due_date + 'T23:59:59') && invoice.status !== 'paid' ? 'text-red-600 font-medium' : ''}>
              {format(new Date(invoice.due_date), 'dd MMMM yyyy')}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default InvoiceDetailsCard;
